import { forwardRef, useEffect, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";

import "./C6TimeSpentAtSea.scss"

import { Button, Label, TextArea } from "abs-ui-components";

import { AUTOSAVE_SUCCESS_MESSAGE, c61SectionFormFields, c62SectionFormFields } from "../../Constants";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import AutoSave from "../../../../../common/AutoSave/AutoSave";

const C6TimeSpentAtSea = forwardRef(({ c61TimeAtSea, vesselMstId,c62IceNavigationTime, refetchMonitoringData }, ref) => {
    const { control, getValues, reset, formState } = useFormContext();
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);

    const { dirtyFields } = formState;
    const isC61Dirty = dirtyFields?.c61 && Object.keys(dirtyFields.c61).length > 0;
    const isC62Dirty = dirtyFields?.c62 && Object.keys(dirtyFields.c62).length > 0;

    useEffect(() => {
        if (c61TimeAtSea) {
            reset(prev => ({ ...prev, c61: c61TimeAtSea }));
        }
        if (c62IceNavigationTime) {
            reset(prev => ({ ...prev, c62: c62IceNavigationTime }));
        }
    }, [c61TimeAtSea, c62IceNavigationTime, reset]);

    AutoSave(ref, [], async () => {
        if (isC61Dirty) await handleSave61(true);
        if (isC62Dirty) await handleSave62(true);
    });

    const handleSave61 = async (isAuto = false) => {
        const formValues = getValues("c61");
        const payload = {
            ...c61SectionFormFields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const c61TimeAtSeaValues = await callApi({
                method: "POST",
                url: API_ROUTES.C61(vesselMstId),
                data: payload,
            });
            if (c61TimeAtSeaValues?.isSuccess) {
                emitToasterMsg("success", "Table C.6.1", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c61: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.6.1", c61TimeAtSeaValues?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.6.1", error.errorMessage);
        }
    };

    const handleSave62 = async (isAuto = false) => {
        const formValues = getValues("c62");
        const payload = {
            ...c62SectionFormFields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const c62IceNavigationTimeValues = await callApi({
                method: "POST",
                url: API_ROUTES.C62(vesselMstId),
                data: payload,
            });
            if (c62IceNavigationTimeValues?.isSuccess) {
                emitToasterMsg("success", "Table C.6.2", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c62: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.6.2", c62IceNavigationTimeValues?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.6.2", error.errorMessage);
        }
    };

    return (
        <>
            <div className="c61-section-form mt-4">
                <MDBRow className="mb-2">
                    <MDBCol md="4">
                        <h6 className="titles-font">Title of procedure</h6>
                    </MDBCol>
                    <MDBCol md="6">
                        <h6 className="titles-font">
                            Determining and recording the time spent at sea from berth of port of departure to berth of the port of arrival</h6>
                    </MDBCol>
                </MDBRow>
                {c61SectionFormFields.map(({ label, name, maxLength }, index) => (
                    <MDBRow className="align-items-center mb-3" key={index}>
                        <MDBCol md="4">
                            <Label name={label} bold />
                        </MDBCol>
                        <MDBCol md="4">
                            <Controller
                                name={`c61.${name}`}
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        id={`c61_${name}`}
                                        className="textarea-resize"
                                        value={field?.value || ""}
                                        onChange={field.onChange}
                                        placeholder=""
                                        maxLength={maxLength}
                                        rows={1}
                                    />
                                )}
                            />
                        </MDBCol>
                    </MDBRow>
                ))}

                <div className="text-center">
                    <Button id="c61_save_btn"
                        disabled={!isC61Dirty}
                        onClick={async () => {
                            manualSaveTriggered.current = true;
                            await handleSave61(false);
                        }}
                    >Save</Button>
                </div>
            </div>

            <div className="mp-headers">
                C6.2 Procedures for determining and recording the time spent at sea when navigating through ice (voluntary monitoring):
            </div>

            <div className="c62-section-form mt-5">
                <MDBRow className="mb-2">
                    <MDBCol md="4">
                        <h6 className="titles-font">Title of procedure</h6>
                    </MDBCol>
                    <MDBCol md="6">
                        <h6 className="titles-font">
                            Determining and recording the time spent at sea when navigating through ice</h6>
                    </MDBCol>
                </MDBRow>
                {c62SectionFormFields.map(({ label, name, maxLength }, index) => (
                    <MDBRow className="align-items-center mb-3" key={index}>
                        <MDBCol md="4">
                            <Label name={label} bold />
                        </MDBCol>
                        <MDBCol md="4">
                            <Controller
                                name={`c62.${name}`}
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        id={`c62_${name}`}
                                        className="textarea-resize"
                                        value={field?.value || ""}
                                        onChange={field.onChange}
                                        placeholder=""
                                        maxLength={maxLength}
                                        rows={1}
                                    />
                                )}
                            />
                        </MDBCol>
                    </MDBRow>
                ))}

                <div className="text-center">
                    <Button id="c62_save_btn"
                        disabled={!isC62Dirty}
                        onClick={async () => {
                            manualSaveTriggered.current = true;
                            await handleSave62(false);
                        }}
                    >Save</Button>
                </div>
            </div>
        </>
    );
});

export default C6TimeSpentAtSea;